class LuxCollectionProduct extends HTMLElement {
    constructor() {
        super();
        this.isDown = false;
        this.startX = 0;
        this.startScrollLeft = 0;
        this.preventClick = false;
        this.initialized = false;
    }

    connectedCallback() {
        // Create the observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.initialized) {
                    this.initCard();
                    this.initialized = true;
                    observer.unobserve(this); // Clean up
                }
            });
        }, { 
            rootMargin: '400px' // Start loading when card is 400px away
        });

        observer.observe(this);
    }

    initCard() {
        // 1. Load the images first
        const lazyImages = this.querySelectorAll('img[data-src]');
        lazyImages.forEach(img => {
            img.src = img.getAttribute('data-src');
            img.removeAttribute('data-src'); // Clean up DOM
        });

        // 2. Select elements for logic
        this.scrollbars = this.querySelectorAll('.lux-collection-product__scrollbar');
        this.header = this.querySelector('.lux-collection-product__header');
        this.gallery = this.querySelector('.lux-collection-product__gallery--inner');
        this.thumbs = this.querySelectorAll('.lux-collection-product__scrollbar-thumb');
        this.slides = this.querySelectorAll('.lux-collection-product__frame');
        this.swatches = this.querySelectorAll('.lux-collection-product__swatch');
        this.primaryImage = this.querySelector('.primary-image');
        this.prevBtn = this.querySelector('.lux-collection-product__nav-arrow--prev');
        this.nextBtn = this.querySelector('.lux-collection-product__nav-arrow--next');


        // 3. Initialize behaviors
        if (this.gallery) {
            this.initDragScroll();
            this.initScrollbar();
            this.initNavArrows();
        }
        this.initEventListeners();
    }

    initEventListeners() {
        // Prevent click after drag
        if (this.header) {
            this.header.addEventListener('click', (e) => {
                if (this.preventClick) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.preventClick = false; 
                }
            });
        }

        // Swatch handling
        if (this.swatches.length) {
            this.swatches.forEach(swatch => {
                const triggerUpdate = (e) => {
                    if(e.type === 'click') {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    this.updateVariantInfo(swatch);
                };
                swatch.addEventListener('click', triggerUpdate);
                swatch.addEventListener('mouseenter', triggerUpdate);
            });
        }
    }

    // --- Drag Scroll & Scrollbar logic remains the same as previous version ---
    // (Ensure you include initDragScroll(), initScrollbar(), 
    // updateThumbPosition(), and updateVariantInfo() here)

    initScrollbar() {
        if (this.thumbs.length === 0 || this.slides.length === 0) return;
        const thumbWidth = 100 / this.slides.length;
        this.thumbs.forEach(thumb => thumb.style.width = `${thumbWidth}%`);
        this.gallery.addEventListener('scroll', () => this.updateThumbPosition());
        this.updateThumbPosition();

        if (this.scrollbars.length != 0) {
            const count = this.slides.length - 1;
            const separator = document.createElement('div');
            const separatorWrapper = document.createElement('div');
            
            separator.classList.add('lux-collection-product__scrollbar-separator');
            separatorWrapper.classList.add('lux-collection-product__scrollbar-separator__wrapper');
            
            for(let i = 0; i < count; i++) {
                const clone = separator.cloneNode(true);
                separatorWrapper.appendChild(clone)
            }

            this.scrollbars.forEach(scrollbar => {
                const clone = separatorWrapper.cloneNode(true);
                scrollbar.appendChild(clone);
            });
        }
    }

    updateThumbPosition() {
        const scrollTotal = this.gallery.scrollWidth - this.gallery.clientWidth;
        if (scrollTotal <= 0) return;
        const scrollPercentage = this.gallery.scrollLeft / scrollTotal;
        const translateX = scrollPercentage * (this.slides.length - 1) * 100;
        this.thumbs.forEach(thumb => thumb.style.transform = `translateX(${translateX}%)`);
    }

    initDragScroll() {
        const slider = this.gallery;
        let isTouch = false;
        let startY = 0;

        const stopDragging = (e, isTouchEvent) => {
            if (!this.isDown) return;
            this.isDown = false;

            slider.classList.remove('is-dragging');

            const endX = isTouchEvent
                ? (e.changedTouches ? e.changedTouches[0].pageX : this.startX)
                : e.pageX;

            const distance = endX - this.startX;
            const threshold = 50; 
            const slideWidth = this.slides[0].offsetWidth;

            // Calculate based on the slide we started on
            const currentIndex = Math.round(this.startScrollLeft / slideWidth);
            let targetIndex = currentIndex;

            // STRICT ONE-SLIDE LOGIC
            if (distance < -threshold) {
                targetIndex = currentIndex + 1;
            } else if (distance > threshold) {
                targetIndex = currentIndex - 1;
            }

            targetIndex = Math.max(0, Math.min(targetIndex, this.slides.length - 1));

            slider.style.scrollSnapType = 'x mandatory';
            slider.style.scrollBehavior = 'smooth';

            slider.scrollTo({
                left: targetIndex * slideWidth
            });
        };

        slider.addEventListener('mousedown', (e) => {
            if (e.button !== 0) return;
            this.isDown = true;
            isTouch = false;
            this.preventClick = false;
            slider.classList.add('is-dragging');
            
            this.startX = e.pageX;
            this.startScrollLeft = slider.scrollLeft;
        });

        slider.addEventListener('touchstart', (e) => {
            this.isDown = true;
            isTouch = true;
            this.preventClick = false;
            slider.classList.add('is-dragging');

            this.startX = e.touches[0].pageX;
            startY = e.touches[0].pageY;
            this.startScrollLeft = slider.scrollLeft;
        }, { passive: true });

        const handleMove = (e) => {
            if (!this.isDown) return;

            const currentX = isTouch ? e.touches[0].pageX : e.pageX;
            const currentY = isTouch ? e.touches[0].pageY : e.pageY;
            
            const walkX = currentX - this.startX;
            const walkY = currentY - startY;

            // 1. Allow vertical page scrolling
            if (isTouch && Math.abs(walkY) > Math.abs(walkX)) {
                this.isDown = false;
                slider.classList.remove('is-dragging');
                return;
            }

            if (Math.abs(walkX) > 5) {
                this.preventClick = true;
                if (e.cancelable) e.preventDefault(); 
            }

            const slideWidth = this.slides[0].offsetWidth;

            // 2. 🔥 THE FIX: VISUAL CLAMPING
            // We limit the 'walk' to the width of one slide.
            // Even if the user drags 1000px, the gallery only moves one slide length.
            const limitedWalk = Math.max(Math.min(walkX, slideWidth), -slideWidth);
            
            slider.scrollLeft = this.startScrollLeft - limitedWalk;
        };

        window.addEventListener('mousemove', (e) => this.isDown && !isTouch && handleMove(e));
        slider.addEventListener('touchmove', (e) => this.isDown && isTouch && handleMove(e), { passive: false });

        window.addEventListener('mouseup', (e) => !isTouch && stopDragging(e, false));
        slider.addEventListener('touchend', (e) => isTouch && stopDragging(e, true));
    }

    initNavArrows() {
        if (!this.prevBtn || !this.nextBtn) return;

        const handleArrowClick = (e, direction) => {
            // 1. Kill the event immediately
            e.preventDefault();
            e.stopPropagation();
            
            // 2. Tell the card header NOT to navigate
            this.preventClick = true;

            // 3. Perform the scroll
            const slideWidth = this.slides[0].offsetWidth;
            const currentScroll = this.gallery.scrollLeft;
            const target = direction === 'next' 
                ? currentScroll + slideWidth 
                : currentScroll - slideWidth;

            this.gallery.scrollTo({
                left: target,
                behavior: 'smooth'
            });

            // 4. Reset preventClick after a short delay to allow normal clicks again
            setTimeout(() => { this.preventClick = false; }, 100);
        };

        this.prevBtn.addEventListener('click', (e) => handleArrowClick(e, 'prev'));
        this.nextBtn.addEventListener('click', (e) => handleArrowClick(e, 'next'));
        
        // Also stop mousedown on arrows from triggering drag logic
        [this.prevBtn, this.nextBtn].forEach(btn => {
            btn.addEventListener('mousedown', (e) => e.stopPropagation());
        });
    }

    updateVariantInfo(swatch) {
        const imgUrl = swatch.getAttribute('data-image');
        const variantUrl = swatch.getAttribute('data-url');
        if (!imgUrl) return;

        this.swatches.forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
        if (this.header) this.header.href = variantUrl;

        // Find slide with this image or update primary
        const targetImg = this.querySelector(`img[src*="${imgUrl}"]`);
        if (targetImg) {
            this.gallery.scrollTo({ left: targetImg.closest('.lux-collection-product__frame').offsetLeft, behavior: 'smooth' });
        } else if (this.primaryImage) {
            this.primaryImage.src = imgUrl;
            this.gallery.scrollTo({ left: 0, behavior: 'smooth' });
        }
    }
}

if (!customElements.get('lux-collection-product')) {
    customElements.define('lux-collection-product', LuxCollectionProduct);
}